import { useState, useEffect } from "react";
import ModifyCart from "../components/Cart/ModifyCart";
import DeleteCart from "../components/Cart/DeleteCart";
import ClearCart from "../components/Cart/ClearCart";
import Commande from "../components/Commande/Commande";
import { useCart } from "../components/Cart/CartContext";
import { useNavigate } from "react-router-dom";
import ThreeModelViewer from "../components/ThreeModelViewer";
import api from "../utils/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaShoppingCart,
  FaTrash,
  FaCheck,
  FaGift,
  FaCreditCard,
} from "react-icons/fa";
import emptyCartIllustration from "../assets/empty-cart.png";



const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};

const normalize = (str) => (str ? str.trim().toLowerCase() : "");

const getVariantByColorAndTaille = (product, couleur, taille) => {
  if (!product || !product.variantes) return null;
  return product.variantes.find(
    (v) =>
      normalize(v.couleur) === normalize(couleur) &&
      v.stock?.some((s) => normalize(s.taille) === normalize(taille))
  );
};

const calculateItemPrice = (product, promoData) => {
  if (!product || !product.price) return { originalPrice: 0, finalPrice: 0 };

  console.log(`🔍 Calcul prix pour ${product.name}:`, { product, promoData });

  // Si le produit a déjà une promotion produit, on utilise son promoPrice
  // ET on applique la promotion panier sur le prix initial
  if (product.promo && product.price.promoPrice) {
    const originalPrice = product.price.originalPrice || product.price;
    let finalPrice = product.price.promoPrice; // Prix après promo produit
    
    // Appliquer la promotion panier sur le prix INITIAL (pas sur le prix promo produit)
    if (promoData?.pourcentage) {
      const panierDiscount = (promoData.pourcentage / 100) * originalPrice;
      finalPrice = Math.max(0, originalPrice - panierDiscount);
      console.log(`💰 Produit avec promo produit + promo panier: Prix initial: ${originalPrice}, Prix promo produit: ${product.price.promoPrice}, Prix final avec promo panier: ${finalPrice}`);
    } else {
      console.log(`💰 Produit avec promo produit uniquement: Prix initial: ${originalPrice}, Prix promo produit: ${product.price.promoPrice}`);
    }
    
    return {
      originalPrice,
      finalPrice,
      hasDiscount: finalPrice < originalPrice,
      isProductPromo: true,
      basePriceForPromo: originalPrice, // Prix initial pour la promo panier
    };
  }

  // Produit sans promotion produit, appliquer uniquement la promotion panier
  const originalPrice = product.price.originalPrice || product.price;
  let finalPrice = originalPrice;

  if (promoData?.pourcentage) {
    const discount = (promoData.pourcentage / 100) * originalPrice;
    finalPrice = Math.max(0, originalPrice - discount);
    console.log(`🎯 Promotion panier appliquée: ${promoData.pourcentage}% de réduction sur ${originalPrice} = ${finalPrice}`);
  } else {
    console.log(`❌ Pas de promotion panier disponible`);
  }

  return {
    originalPrice,
    finalPrice,
    hasDiscount: finalPrice < originalPrice,
    isProductPromo: false,
    basePriceForPromo: originalPrice,
  };
};

const EmptyCart = () => {
  const navigate = useNavigate();

  return (
    <>
      <div style={{ height: "12vh" }}></div>

      <motion.div
        className="empty-cart-container"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="empty-cart-content"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <motion.img
            src={emptyCartIllustration}
            alt="Panier vide"
            className="empty-cart-illustration"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
          <h3 className="empty-cart-title">Votre panier est vide</h3>
          <p className="empty-cart-message">
            Explorez notre collection artisanale pour découvrir des babouches
            uniques
          </p>
          <motion.button
            onClick={() => navigate("/products/femme")}
            className="empty-cart-button"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaShoppingCart />
            Découvrir nos produits
          </motion.button>
        </motion.div>

        <style jsx>{`
          .empty-cart-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 70vh;
            padding: 40px 20px;
            text-align: center;
            background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
            font-family: "Cormorant Garamond", serif;
          }

          .empty-cart-content {
            max-width: 500px;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(20px);
            padding: 40px 20px;
            border-radius: 20px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(212, 175, 55, 0.3);
            display: flex;
            justify-content: center;
            flex-direction: column;
            align-items: center;
          }

          .empty-cart-illustration {
            width: 200px;
            height: auto;
            margin-bottom: 30px;
            opacity: 0.8;
            filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.1));
          }

          .empty-cart-title {
            font-size: 2.5rem;
            color: #2c1810;
            margin-bottom: 20px;
            font-weight: 600;
            letter-spacing: 1px;
            font-family: "Cormorant Garamond", serif;
          }

          .empty-cart-message {
            font-size: 1.2rem;
            color: #5a4a3a;
            margin-bottom: 40px;
            line-height: 1.6;
            font-style: italic;
            font-family: "Cormorant Garamond", serif;
          }

          .empty-cart-button {
            padding: 15px 30px;
            background: rgba(212, 175, 55, 0.2);
            color: #2c1810;
            border: 1px solid rgba(212, 175, 55, 0.3);
            border-radius: 15px;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .empty-cart-button:hover {
            background: rgba(212, 175, 55, 0.3);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
          }

          @media (max-width: 768px) {
            .empty-cart-content {
              padding: 40px 20px;
            }

            .empty-cart-title {
              font-size: 2rem;
            }

            .empty-cart-message {
              font-size: 1rem;
            }
          }
        `}</style>
      </motion.div>
    </>
  );
};

export default function Cart() {
  const { items, total, loading, error, fetchCart, refreshCartCount } =
    useCart();
  const [localItems, setLocalItems] = useState([]);
  const [selectedItemsMap, setSelectedItemsMap] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoData, setPromoData] = useState(null);
  const [promoMessage, setPromoMessage] = useState("");
  const [isProductPromo, setIsProductPromo] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    setLocalItems(items);
    const newSelected = {};
    items.forEach((item, index) => {
      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
      newSelected[key] = true;
    });
    setSelectedItemsMap(newSelected);
    setSelectAll(true);
  }, [items]);

  const handleSelectAll = () => {
    const newState = !selectAll;
    const newSelected = {};
    localItems.forEach((item, index) => {
      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
      newSelected[key] = newState;
    });
    setSelectedItemsMap(newSelected);
    setSelectAll(newState);
  };

  const handleSelectItem = (key) => {
    setSelectedItemsMap((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      const allSelected = Object.values(updated).every(Boolean);
      setSelectAll(allSelected);
      return updated;
    });
  };

  const calculateSelectedTotal = () => {
    console.log("🔍 Calcul du total avec promoData:", promoData);
    
    return localItems.reduce((acc, item, index) => {
      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
      if (selectedItemsMap[key]) {
        // 🔍 VÉRIFIER QUE LE PRODUIT EXISTE AVANT DE CALCULER
        if (!item.product || !item.product._id) {
          console.warn("⚠️ Article avec produit null ignoré dans le calcul:", item);
          return acc;
        }
        
        // ✅ CORRECTION : On applique TOUJOURS la promotion panier
        // Même si le produit a déjà une promotion produit
        const { finalPrice, originalPrice } = calculateItemPrice(item.product, promoData);
        
        if (item.product?.promo && item.product?.price?.promoPrice) {
          console.log(`💰 Produit avec promo produit + promo panier: ${item.product.name} - Prix promo produit: ${item.product.price.promoPrice} - Prix final avec promo panier: ${finalPrice}`);
        } else {
          console.log(`🛒 Produit sans promo produit: ${item.product.name} - Prix original: ${originalPrice} - Prix final avec promo panier: ${finalPrice}`);
        }
        
        return acc + finalPrice * item.quantity;
      }
      return acc;
    }, 0);
  };
  const getSelectedItems = () => {
    return localItems.filter((item, index) => {
      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
      // 🔍 FILTRER LES ARTICLES AVEC DES PRODUITS NULL
      if (!item.product || !item.product._id) {
        console.warn("⚠️ Article avec produit null détecté dans le panier:", item);
        return false;
      }
      return selectedItemsMap[key];
    });
  };

  const handleItemUpdated = (
    productId,
    oldTaille,
    oldCouleur,
    newTaille,
    newCouleur,
    newQuantity
  ) => {
    setLocalItems((prev) =>
      prev.map((item) => {
        if (
          item.product?._id === productId &&
          item.taille === oldTaille &&
          item.couleur === oldCouleur
        ) {
          return {
            ...item,
            taille: newTaille || oldTaille,
            couleur: newCouleur || oldCouleur,
            quantity: newQuantity,
          };
        }
        return item;
      })
    );
    refreshCartCount();
  };

  const handleApplyPromo = async () => {
    setPromoMessage("⏳ Vérification...");
    try {
              const res = await api.post(`/api/promos/validate`, {
        code: promoCode,
      });

      setPromoData(res.data);
      setPromoMessage(`🎉 Code promo "${promoCode.toUpperCase()}" appliqué ! Vous économisez ${res.data.pourcentage}% sur votre commande.`);
    } catch (err) {
      setPromoData(null);
      setPromoMessage(err.response?.data?.error || "❌ Code invalide.");
    }
  };

  const handleCancelPromo = () => {
    setPromoData(null);
    setPromoCode("");
    setPromoMessage("❌ Code promo annulé.");
  };

  if (loading) {
    return (
      <motion.div
        className="loading-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="loading-spinner" />
        <p className="loading-text">Chargement de votre panier...</p>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="error-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <p className="error-message">{error}</p>
      </motion.div>
    );
  }

  if (!localItems || localItems.length === 0) return <EmptyCart />;

  const fallbackImageUrl = "https://placehold.co/100x100?text=No+Image";

  return (
    <>
      <div style={{ height: window.innerWidth <= 768 ? "7vh" : "7vh" }}></div>

      <motion.div
        className="cart-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div
          style={{ height: window.innerWidth <= 768 ? "0vh" : "5vh" }}
        ></div>

        <div className="cart-content">
          {/* Sélectionner tout */}
          <motion.div
            className="select-all-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            style={{
              padding: window.innerWidth <= 768 ? "15px 10px" : "20px",
              gap: window.innerWidth <= 768 ? "10px" : "15px"
            }}
          >
            <div className="select-all-left" onClick={handleSelectAll} style={{
              textAlign: window.innerWidth <= 768 ? "center" : "left"
            }}>
           
              <label className="select-all-label" style={{
                fontSize: window.innerWidth <= 768 ? "13px" : "16px",
                lineHeight: window.innerWidth <= 768 ? "1.4" : "1.5"
              }}> 
                {window.innerWidth <= 768 
                  ? "Sélectionnez les produits à commander" 
                  : "Les produits sélectionnés sont les produits que vous souhaitez commander"
                }
              </label>
            </div>

            <div style={{ 
              display: "flex", 
              gap: window.innerWidth <= 768 ? "8px" : "15px", 
              alignItems: "center",
              flexWrap: window.innerWidth <= 768 ? "wrap" : "nowrap",
              justifyContent: window.innerWidth <= 768 ? "center" : "flex-end",
              width: window.innerWidth <= 768 ? "100%" : "auto"
            }}>
              {selectAll ? (
                <motion.button
                  className="deselect-all-button"
                  onClick={() => {
                    const newSelected = {};
                    localItems.forEach((item, index) => {
                      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                      newSelected[key] = false;
                    });
                    setSelectedItemsMap(newSelected);
                    setSelectAll(false);
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: window.innerWidth <= 768 ? "8px 12px" : "10px 16px",
                    fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                    whiteSpace: window.innerWidth <= 768 ? "nowrap" : "normal",
                    minWidth: window.innerWidth <= 768 ? "fit-content" : "auto",
                    maxWidth: window.innerWidth <= 768 ? "140px" : "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  <FaTrash />
                  {window.innerWidth <= 768 ? "Désélectionner" : "Tout désélectionner"}
                </motion.button>
              ) : (
                <motion.button
                  className="select-all-button"
                  onClick={() => {
                    const newSelected = {};
                    localItems.forEach((item, index) => {
                      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                      newSelected[key] = true;
                    });
                    setSelectedItemsMap(newSelected);
                    setSelectAll(true);
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: window.innerWidth <= 768 ? "8px 12px" : "10px 16px",
                    fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                    whiteSpace: window.innerWidth <= 768 ? "nowrap" : "normal",
                    minWidth: window.innerWidth <= 768 ? "fit-content" : "auto",
                    maxWidth: window.innerWidth <= 768 ? "140px" : "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  <FaCheck />
                  {window.innerWidth <= 768 ? "Sélectionner" : "Sélectionner tout"}
                </motion.button>
              )}
              
              {/* Bouton pour vider le panier */}
              {localItems.length > 0 && (
                <ClearCart onCartCleared={fetchCart} />
              )}
            </div>
          </motion.div>

          {/* Liste des produits */}
          <motion.ul
            className="cart-items-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <AnimatePresence>
              {localItems.map(({ product, quantity, taille, couleur }, i) => {
                if (!product) return null;
                const key = `${product._id}-${couleur}-${taille}-${i}`;
                const variante = getVariantByColorAndTaille(
                  product,
                  couleur,
                  taille
                );
                const firstImage = variante?.images?.[0];
                const is3D = firstImage?.match(/\.(glb|gltf|obj)$/i);
                const imageUrl = firstImage
                  ? buildImageUrl(firstImage)
                  : fallbackImageUrl;

                const { originalPrice, finalPrice, hasDiscount } =
                  calculateItemPrice(product, promoData);

                return (
                  <motion.li
                    key={key}
                    className="cart-item"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 50 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedItemsMap[key]}
                      onChange={() => handleSelectItem(key)}
                      className="item-checkbox"
                    />

                    <motion.div
                      className="item-image-container"
                      onClick={() => navigate(`/product/${product._id}`)}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      {is3D ? (
                        <ThreeModelViewer
                          url={imageUrl}
                          width={120}
                          height={120}
                        />
                      ) : (
                        <img
                          src={imageUrl}
                          alt={product.name}
                          className="item-image"
                          onError={(e) =>
                            (e.currentTarget.src = fallbackImageUrl)
                          }
                        />
                      )}
                    </motion.div>

                    <div className="item-details">
                      <h3
                        onClick={() => navigate(`/product/${product._id}`)}
                        className="item-title"
                      >
                        {product.name}
                      </h3>
                      <div className="item-price">
                        {hasDiscount ? (
                          <>
                            <span className="original-price">
                              ~~{originalPrice.toFixed(2)} MAD~~
                            </span>
                            <span
                              className={`final-price ${
                                isProductPromo ? "product-promo" : "cart-promo"
                              }`}
                            >
                              🎯 {finalPrice.toFixed(2)} MAD
                              {isProductPromo && (
                                <span className="promo-badge">Promo</span>
                              )}
                              {!isProductPromo && promoData?.pourcentage && (
                                <span className="promo-badge cart-promo-badge">
                                  -{promoData.pourcentage}%
                                </span>
                              )}
                            </span>
                            <div className="savings-per-item">
                              💰 Économie : {(originalPrice - finalPrice).toFixed(2)} MAD
                            </div>
                          </>
                        ) : (
                          <span>{originalPrice.toFixed(2)} MAD</span>
                        )}
                      </div>
                      <div className="item-info">
                        <span className="info-tag">🎨 {couleur}</span>
                        <span className="info-tag">📏 {taille}</span>
                        <span className="info-tag">📦 {quantity}</span>
                      </div>

                      <ModifyCart
                        productId={product._id}
                        initialTaille={taille}
                        initialCouleur={couleur}
                        initialQuantity={quantity}
                        productVariantes={product.variantes}
                        onUpdated={({ taille, couleur, quantity }) =>
                          handleItemUpdated(
                            product._id,
                            taille,
                            couleur,
                            taille,
                            couleur,
                            quantity
                          )
                        }
                      />
                    </div>

                    <DeleteCart
                      productId={product._id}
                      taille={taille}
                      couleur={couleur}
                      onDeleted={fetchCart}
                    />
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </motion.ul>

          {/* Section Promo */}
          <motion.div
            className="promo-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <div className="promo-card">
              <h4 className="promo-title">
                <FaGift />
                Code promo
              </h4>
              <div className="promo-input-container">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Ex : SUMMER25"
                  className="promo-input"
                  disabled={!!promoData}
                />
                {!promoData ? (
                  <motion.button
                    onClick={handleApplyPromo}
                    className="promo-apply-btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Appliquer
                  </motion.button>
                ) : (
                  <motion.button
                    onClick={handleCancelPromo}
                    className="promo-cancel-btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Annuler
                  </motion.button>
                )}
              </div>
              {promoMessage && (
                <motion.p
                  className={`promo-message ${promoData ? "success" : "error"}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {promoMessage}
                </motion.p>
              )}
              {promoData?.pourcentage && (
                <motion.div
                  className="promo-success-banner"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="promo-success-content">
                    <h4>Code promo appliqué</h4>
                    <p>Vous économisez {promoData.pourcentage}% sur votre commande</p>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Totaux */}
          <motion.div
            className="totals-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <div className="totals-card">
             

              {localItems.some((item) => item.product?.promo) && (
                <motion.div
                  className="discount-info product-discount"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  Réductions produits appliquées
                </motion.div>
              )}

              {promoData?.pourcentage && (
                <motion.div
                  className="discount-info cart-discount"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {promoData.pourcentage}% de réduction panier appliquée
                </motion.div>
              )}

              <div className="cart-total">
                {promoData?.pourcentage && (
                  <div className="total-breakdown">
                    <div className="total-original">
                      Total original : {localItems.reduce((acc, item, index) => {
                        const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                        if (selectedItemsMap[key] && item.product?.price) {
                          const originalPrice = item.product.price.originalPrice || item.product.price;
                          return acc + originalPrice * item.quantity;
                        }
                        return acc;
                      }, 0).toFixed(2)} MAD
                    </div>
                    <div className="total-discount">
                      Réduction {promoData.pourcentage}% : -{(
                        localItems.reduce((acc, item, index) => {
                          const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                          if (selectedItemsMap[key] && item.product?.price) {
                            const originalPrice = item.product.price.originalPrice || item.product.price;
                            return acc + originalPrice * item.quantity;
                          }
                          return acc;
                        }, 0) * (promoData.pourcentage / 100)
                      ).toFixed(2)} MAD
                    </div>
                    <div className="savings-highlight">
                      Vous économisez {(
                        localItems.reduce((acc, item, index) => {
                          const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                          if (selectedItemsMap[key] && item.product?.price) {
                            const originalPrice = item.product.price.originalPrice || item.product.price;
                            return acc + originalPrice * item.quantity;
                          }
                          return acc;
                        }, 0) * (promoData.pourcentage / 100)
                      ).toFixed(2)} MAD
                    </div>
                  </div>
                )}
                <div className="total-final">
                  Total final : {promoData?.pourcentage ? 
                    (localItems.reduce((acc, item, index) => {
                      const key = `${item.product?._id}-${item.couleur}-${item.taille}-${index}`;
                      if (selectedItemsMap[key] && item.product?.price) {
                        const originalPrice = item.product.price.originalPrice || item.product.price;
                        return acc + originalPrice * item.quantity;
                      }
                      return acc;
                    }, 0) * (1 - promoData.pourcentage / 100)).toFixed(2) 
                    : calculateSelectedTotal().toFixed(2)} MAD
                </div>
              </div>
            </div>
          </motion.div>
        


          <Commande
            selectedItems={getSelectedItems()}
            totalWithPromo={calculateSelectedTotal()}
            promoData={promoData}
            promoCode={promoCode}
            onSuccess={fetchCart}
          />
        </div>

        {/* 🛍️ BOUTON "VOIR LES PRODUITS" QUAND LE PANIER EST VIDE */}
        {total === 0 && (
          <motion.div
            className="empty-cart-action"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            style={{
              textAlign: "center",
              marginTop: "40px",
              marginBottom: "60px"
            }}
          >
            <motion.button
              className="see-products-btn promo-apply-btn"
              onClick={() => navigate("/products/femme")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🛍️ Voir nos produits
            </motion.button>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              style={{
                marginTop: "20px",
                color: "#2d5016",
                fontSize: "1rem",
                fontFamily: "'Cormorant Garamond', serif"
              }}
            >
              Découvrez notre collection de babouches artisanales
            </motion.p>
          </motion.div>
        )}

        <style jsx>{`
          .cart-container {
            font-family: "Cormorant Garamond", serif;
            color: #2c1810;
            overflow-x: hidden;
            background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
          }

          /* Hero Section */
          .cart-hero {
            position: relative;
            height: 50vh;
            min-height: 400px;
            background: linear-gradient(
              135deg,
              rgba(139, 90, 43, 0.8) 0%,
              rgba(90, 57, 33, 0.9) 100%
            );
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            text-align: center;
            margin-bottom: 60px;
            overflow: hidden;
          }

          .hero-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><path fill="white" fill-opacity="0.05" d="M50,0 C60,20 80,30 100,50 C80,70 60,80 50,100 C40,80 20,70 0,50 C20,30 40,20 50,0 Z"></path></svg>');
            background-size: 150px;
          }

          .hero-content {
            position: relative;
            z-index: 2;
            padding: 0 20px;
          }

          .hero-content h1 {
            font-size: 3.5rem;
            font-weight: 300;
            margin-bottom: 20px;
            letter-spacing: 2px;
            text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
          }

          .hero-content p {
            font-size: 1.3rem;
            font-style: italic;
            text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.3);
          }

          .divider {
            height: 2px;
            width: 100px;
            background: #d4af37;
            margin: 25px auto;
            transform-origin: center;
          }

          .cart-content {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
            gap: 5px;
          }

          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 60vh;
            color: #2c1810;
          }

          .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid rgba(212, 175, 55, 0.2);
            border-top: 3px solid #d4af37;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }

          .loading-text {
            font-size: 1.2rem;
            font-weight: 600;
            font-family: "Cormorant Garamond", serif;
          }

          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }

          .error-container {
            text-align: center;
            padding: 40px 20px;
            color: #d32f2f;
          }

          .error-message {
            font-size: 1.2rem;
            background: rgba(211, 47, 47, 0.1);
            padding: 20px;
            border-radius: 10px;
            border: 1px solid rgba(211, 47, 47, 0.3);
            font-family: "Cormorant Garamond", serif;
          }

          .select-all-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 25px;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(212, 175, 55, 0.2);
            border-radius: 20px;
            padding: 16px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            gap: 14px;
          }

          .select-all-left {
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .select-all-left:hover {
            transform: translateY(-1px);
          }

          .deselect-all-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: rgba(211, 47, 47, 0.1);
            color: #d32f2f;
            border: 1px solid rgba(211, 47, 47, 0.3);
            border-radius: 12px;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.3s ease;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .deselect-all-button:hover {
            background: rgba(211, 47, 47, 0.2);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(211, 47, 47, 0.3);
          }

          .select-all-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: rgba(76, 175, 80, 0.1);
            color: #4caf50;
            border: 1px solid rgba(76, 175, 80, 0.3);
            border-radius: 12px;
            font-weight: 600;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.3s ease;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .select-all-button:hover {
            background: rgba(76, 175, 80, 0.2);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
          }

          .select-all-checkbox {
            width: 20px;
            height: 20px;
            accent-color: #d4af37;
            cursor: pointer;
          }

          .select-all-label {
            font-weight: 600;
            font-size: 1rem;
            color: #2c1810;
            font-family: "Cormorant Garamond", serif;
          }

          .cart-items-list {
            list-style-type: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 15px;
          }

          .cart-item {
            display: flex;
            align-items: center;
            gap: 20px;
            padding: 20px;
            border-radius: 20px;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(212, 175, 55, 0.2);
            transition: all 0.3s ease;
            position: relative;
          }

          .item-checkbox {
            width: 20px;
            height: 20px;
            accent-color: #d4af37;
            cursor: pointer;
          }

          .item-image-container {
            width: 100px;
            height: 100px;
            overflow: hidden;
            border-radius: 15px;
            border: 1px solid rgba(212, 175, 55, 0.3);
            cursor: pointer;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }

          .item-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          }

          .item-details {
            flex-grow: 1;
            min-width: 200px;
          }

          .item-title {
            cursor: pointer;
            margin-bottom: 8px;
            font-size: 1.2rem;
            font-weight: 600;
            color: #2c1810;
            transition: color 0.2s;
            font-family: "Cormorant Garamond", serif;
          }

          .item-title:hover {
            color: #d4af37;
          }

          .item-price {
            font-size: 1.1rem;
            font-weight: 600;
            color: #d4af37;
            margin-bottom: 12px;
            font-family: "Cormorant Garamond", serif;
          }

          .item-price .original-price {
            text-decoration: line-through;
            color: #999;
            margin-right: 8px;
            font-size: 0.9rem;
          }

          .item-price .final-price {
            color: #d32f2f;
            font-weight: bold;
          }

          .item-info {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-bottom: 12px;
          }

          .info-tag {
            background: rgba(212, 175, 55, 0.1);
            color: #2c1810;
            padding: 4px 10px;
            border-radius: 15px;
            font-size: 0.85rem;
            font-weight: 500;
            border: 1px solid rgba(212, 175, 55, 0.2);
            font-family: "Cormorant Garamond", serif;
          }

          .promo-section {
            margin-top: 30px;
          }

          .promo-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            padding: 25px;
            border-radius: 20px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(212, 175, 55, 0.2);
          }

          .promo-title {
            color: #2c1810;
            margin-bottom: 15px;
            font-size: 1.2rem;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: "Cormorant Garamond", serif;
          }

          .promo-input-container {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
          }

          .promo-input {
            padding: 10px 16px;
            flex-grow: 1;
            border: 1px solid rgba(212, 175, 55, 0.2);
            border-radius: 15px;
            font-size: 16px;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            font-family: "Cormorant Garamond", serif;
            color: #2c1810;
          }

          .promo-input:focus {
            outline: none;
            border-color: #d4af37;
            box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
            background: rgba(255, 255, 255, 0.15);
          }

          .promo-apply-btn {
            padding: 10px 20px;
            cursor: pointer;
            background: rgba(212, 175, 55, 0.2);
            color: #2c1810;
            border: 1px solid rgba(212, 175, 55, 0.3);
            border-radius: 15px;
            font-weight: 600;
            transition: all 0.3s ease;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .promo-apply-btn:hover {
            background: rgba(212, 175, 55, 0.3);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
          }

          .promo-cancel-btn {
            padding: 10px 20px;
            cursor: pointer;
            background: rgba(212, 175, 55, 0.1);
            color: #d32f2f;
            border: 1px solid rgba(212, 175, 55, 0.2);
            border-radius: 15px;
            font-weight: 600;
            transition: all 0.3s ease;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .promo-cancel-btn:hover {
            background: rgba(212, 175, 55, 0.2);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
          }

          .promo-message {
            margin-top: 12px;
            font-weight: 600;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 16px;
            font-family: "Cormorant Garamond", serif;
          }

          .promo-message.success {
            color: #4caf50;
            background: rgba(76, 175, 80, 0.1);
            border: 1px solid rgba(76, 175, 80, 0.3);
          }

          .promo-message.error {
            color: #d32f2f;
            background: rgba(211, 47, 47, 0.1);
            border: 1px solid rgba(211, 47, 47, 0.3);
          }

          .totals-section {
            margin-top: 30px;
          }

          .totals-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            padding: 25px;
            border-radius: 20px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
            border: 1px solid rgba(212, 175, 55, 0.2);
            text-align: right;
          }

          .selected-total {
            font-size: 2.2rem;
            font-weight: 600;
            color: #d4af37;
            margin-bottom: 12px;
            font-family: "Cormorant Garamond", serif;
          }

          .selected-total-with-promo {
            font-size: 2.2rem;
            color: #4caf50;
            font-weight: 700;
            text-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
          }

          .discount-info {
            font-size: 1rem;
            color: #4caf50;
            margin-bottom: 8px;
            padding: 8px 12px;
            background: rgba(76, 175, 80, 0.1);
            border-radius: 6px;
            border: 1px solid rgba(76, 175, 80, 0.3);
            font-family: "Cormorant Garamond", serif;
          }

          .cart-total {
            font-size: 1.1rem;
            color: #5a4a3a;
            margin-top: 20px;
            padding-top: 20px;
            border-top: 1px solid rgba(212, 175, 55, 0.2);
            font-family: "Cormorant Garamond", serif;
          }

          .total-breakdown {
            margin-bottom: 15px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #dee2e6;
          }

          .total-original {
            font-size: 1rem;
            color: #6c757d;
            text-decoration: line-through;
            margin-bottom: 8px;
          }

          .total-discount {
            font-size: 1rem;
            color: #28a745;
            font-weight: 600;
            margin-bottom: 12px;
          }

          .total-final {
            font-size: 1.3rem;
            color: #2c1810;
            font-weight: 700;
            padding: 10px 15px;
            background: #f8f9fa;
            border-radius: 8px;
            border: 1px solid #dee2e6;
          }

          .savings-highlight {
            font-size: 1rem;
            color: #28a745;
            font-weight: 600;
            margin-top: 12px;
            padding: 8px 12px;
            background: #d4edda;
            border-radius: 6px;
            border: 1px solid #c3e6cb;
            font-family: "Cormorant Garamond", serif;
          }

          @media (max-width: 768px) {
            .cart-hero {
              height: 40vh;
              min-height: 300px;
            }

            .hero-content h1 {
              font-size: 2.5rem;
            }

            .hero-content p {
              font-size: 1.1rem;
            }

            .cart-content {
              padding: 0 15px;
            }

            .cart-item {
              flex-direction: column;
              align-items: flex-start;
              gap: 20px;
            }

            .item-image-container {
              width: 100%;
              height: 200px;
            }

            .item-details {
              width: 100%;
            }

            .promo-input-container {
              flex-direction: column;
            }

            .promo-input,
            .promo-apply-btn,
            .promo-cancel-btn {
              width: 100%;
            }

            .selected-total {
              font-size: 2rem;
            }
          }

          .item-price .final-price.product-promo {
            color: #d4af37; /* Couleur or pour les promos produits */
          }

          .item-price .final-price.cart-promo {
            color: #d32f2f; /* Couleur rouge pour les promos panier */
          }

          .promo-badge {
            font-size: 0.7rem;
            background: #d4af37;
            color: white;
            padding: 2px 6px;
            border-radius: 10px;
            margin-left: 8px;
            vertical-align: middle;
          }

          .discount-info.product-discount {
            background: rgba(212, 175, 55, 0.1);
            border: 1px solid rgba(212, 175, 55, 0.3);
            color: #d4af37;
          }

          .discount-info.cart-discount {
            background: rgba(76, 175, 80, 0.1);
            border: 1px solid rgba(76, 175, 80, 0.3);
            color: #4caf50;
          }

          .savings-per-item {
            font-size: 0.9rem;
            color: #d32f2f;
            margin-top: 8px;
            font-weight: 600;
            font-family: "Cormorant Garamond", serif;
          }

          .promo-badge.cart-promo-badge {
            background: #d32f2f;
            color: white;
            padding: 2px 6px;
            border-radius: 10px;
            margin-left: 8px;
            vertical-align: middle;
          }

          .promo-success-banner {
            display: flex;
            align-items: center;
            background: #f8f9fa;
            color: #2c1810;
            padding: 20px;
            border-radius: 8px;
            margin: 20px 0;
            border: 1px solid #dee2e6;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .promo-success-content h4 {
            margin: 0 0 8px 0;
            font-size: 1.2rem;
            font-weight: 600;
            color: #2c1810;
            font-family: "Cormorant Garamond", serif;
          }

          .promo-success-content p {
            margin: 0;
            font-size: 1rem;
            color: #6c757d;
            font-family: "Cormorant Garamond", serif;
          }

          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }

          .see-products-btn {
            padding: 16px 32px;
            cursor: pointer;
            background: rgba(212, 175, 55, 0.2);
            color: #2c1810;
            border: 1px solid rgba(212, 175, 55, 0.3);
            border-radius: 15px;
            font-weight: 600;
            transition: all 0.3s ease;
            font-family: "Cormorant Garamond", serif;
            backdrop-filter: blur(10px);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 1.1rem;
          }

          .see-products-btn:hover {
            background: rgba(212, 175, 55, 0.3);
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(212, 175, 55, 0.3);
          }

          .promo-apply-btn {
          }
        `}</style>
      </motion.div>

      <div style={{ height: window.innerWidth <= 768 ? "7vh" : "7vh" }}></div>
    </>
  );
}
